import React from 'react';
import type { Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { formatDateMain, getCarrierAmount, getCarrierLeg } from '@hero/core';
import { useOrderDestination } from '@hero/hooks-shared';
import { VehicleName } from '../../Vehicle/VehicleName';
import { Price } from '../../../app/Price';
import { CalendarIcon, ArrowRightIcon, PackageIcon } from '@hero/web-icons';
import { RefDisplay } from '../../../app/Ref/RefDisplay';

export interface PublishedOrderCardProps {
  order: Order;
  onClick: (order: Order) => void;
}

export function PublishedOrderCard({ order, onClick }: PublishedOrderCardProps) {
  const { t } = useTranslation(['m-order']);
  const { firstPickupDisplay, lastDropoffDisplay } = useOrderDestination(order.destinations ?? []);

  const pickupCity = firstPickupDisplay?.city || '';
  const pickupNeighborhood = firstPickupDisplay?.neighborhood || firstPickupDisplay?.country || '';
  const dropoffCity = lastDropoffDisplay?.city || '';
  const dropoffNeighborhood = lastDropoffDisplay?.neighborhood || lastDropoffDisplay?.country || '';

  const vehicleType = order.load?.vehicles?.[0];
  const pickupDate = order.dates?.pickup?.from;
  const carrierAmount = getCarrierLeg(order) ? getCarrierAmount(order) : undefined;

  const itemNames = order.load?.items
    ?.map(i => i.name)
    .filter(Boolean)
    .slice(0, 2)
    .join(', ');
  const itemDisplay = order.load?.items?.length > 2 ? `${itemNames}...` : itemNames;

  return (
    <button
      type="button"
      onClick={() => onClick(order)}
      className="w-full text-start bg-white border border-gray-200 rounded-xl p-4 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer group">
      <div className="flex flex-col gap-3">
        {/* Price + Book Now */}
        <div className="flex items-center justify-between">
          {carrierAmount ? (
            <Price
              amount={carrierAmount.itemsOriginalSubtotal}
              currency={carrierAmount.currency}
              className="font-semibold text-primary/70"
            />
          ) : (
            <span />
          )}
          <span className="text-xs font-medium bg-success-50 text-success-500 px-2.5 py-0.5 rounded-full">
            {t('m-order:published_orders.book_now')}
          </span>
        </div>

        {/* Route */}
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground truncate">{pickupNeighborhood}</p>
            <p className="text-base font-semibold truncate">{pickupCity || '-'}</p>
          </div>

          <div className="flex flex-col items-center shrink-0 gap-0.5 px-1">
            {vehicleType && (
              <span className="text-[11px] text-muted-foreground leading-none">
                <VehicleName vehicle={vehicleType} />
              </span>
            )}
            <ArrowRightIcon className="w-5 h-5 text-gray-400 rtl:rotate-180" />
          </div>

          <div className="min-w-0 flex-1 text-end">
            <p className="text-xs text-muted-foreground truncate">{dropoffNeighborhood}</p>
            <p className="text-base font-semibold truncate">{dropoffCity || '-'}</p>
          </div>
        </div>

        {/* Details footer */}
        <div className="flex items-center justify-evenly pt-2.5 border-t border-gray-100 text-xs text-muted-foreground">
          <RefDisplay refShort={order.ref?.short} className="text-xs" />
          {pickupDate && (
            <div className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                {formatDateMain({
                  date: pickupDate,
                  format: 'relative',
                  ifDateisNotRelative: 'date_time',
                })}
              </span>
            </div>
          )}
          {itemDisplay && (
            <div className="flex items-center gap-1 min-w-0">
              <PackageIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate max-w-[100px]">{itemDisplay}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
